import React, { useState } from 'react';
import { theme } from './theme';

/* ============================================================================
 * ChamberRenameModal — rename chambers per-warehouse with icon + description.
 *
 * Value stored on the backend (under the existing "area" alias bucket):
 *
 *   area["<WAREHOUSE>-<CHAMBER>"] = JSON.stringify({ icon: "thermo", desc: "Temperature Controlled" })
 *
 * Backward-compatible: if a value is a plain string (legacy global area
 * rename), parseChamberAlias() returns { icon: '', desc: <string> }.
 * ========================================================================== */

export const CHAMBER_ICONS: Array<{ id: string; emoji: string; bg: string; label: string }> = [
  { id: '',        emoji: '–',  bg: '#f1f5f9', label: 'No icon' },
  { id: 'thermo',  emoji: '🧪', bg: '#ede9fe', label: 'Temperature Controlled' },
  { id: 'cold',    emoji: '❄',  bg: '#dbeafe', label: 'Cold Room' },
  { id: 'chem',    emoji: '⚗',  bg: '#fed7aa', label: 'Chemical Room' },
  { id: 'danger',  emoji: '⚠',  bg: '#fecaca', label: 'Dangerous Goods' },
  { id: 'freezer', emoji: '🧊', bg: '#cffafe', label: 'Freezer' },
  { id: 'box',     emoji: '📦', bg: '#e2e8f0', label: 'Storage / Other' },
];

const ICON_BY_ID: Record<string, { emoji: string; bg: string; label: string }> = {};
CHAMBER_ICONS.forEach(i => { ICON_BY_ID[i.id] = { emoji: i.emoji, bg: i.bg, label: i.label }; });

export function parseChamberAlias(raw: string): { icon: string; desc: string } {
  if (!raw) return { icon: '', desc: '' };
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object' && ('icon' in obj || 'desc' in obj)) {
      return { icon: String(obj.icon || ''), desc: String(obj.desc || '') };
    }
  } catch { /* not JSON — fall through */ }
  return { icon: '', desc: raw };
}

export function encodeChamberAlias(icon: string, desc: string): string {
  if (!icon && !desc.trim()) return '';
  return JSON.stringify({ icon, desc: desc.trim() });
}

/** Small icon swatch — used in the dropdown row AND the picker. */
export const ChamberIcon: React.FC<{ icon: string; size?: number }> = ({ icon, size = 28 }) => {
  const meta = ICON_BY_ID[icon] || ICON_BY_ID['box'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: 6,
      background: meta.bg, fontSize: Math.round(size * 0.55), flexShrink: 0,
      lineHeight: 1,
    }}>{meta.emoji}</span>
  );
};

type Props = {
  codes: string[];                                   // composite codes "Warehouse-Chamber"
  currentAliases: Record<string, string>;            // raw stored values
  onSave: (next: Record<string, string>) => void;    // returns JSON-encoded values
  onClose: () => void;
};

const ChamberRenameModal: React.FC<Props> = ({ codes, currentAliases, onSave, onClose }) => {
  const initial = () => {
    const ic: Record<string, string> = {};
    const de: Record<string, string> = {};
    codes.forEach(c => {
      const parsed = parseChamberAlias(currentAliases[c] || '');
      ic[c] = parsed.icon;
      de[c] = parsed.desc;
    });
    return { ic, de };
  };
  const seed = initial();
  const [iconDraft, setIconDraft] = useState<Record<string, string>>(seed.ic);
  const [descDraft, setDescDraft] = useState<Record<string, string>>(seed.de);
  const [saving, setSaving] = useState(false);

  const clearRow = (code: string) => {
    setIconDraft(d => ({ ...d, [code]: '' }));
    setDescDraft(d => ({ ...d, [code]: '' }));
  };
  const clearAll = () => { setIconDraft({}); setDescDraft({}); };

  const handleSave = async () => {
    setSaving(true);
    const out: Record<string, string> = {};
    codes.forEach(c => {
      const encoded = encodeChamberAlias(iconDraft[c] || '', descDraft[c] || '');
      if (encoded) out[c] = encoded;
    });
    await onSave(out);
    setSaving(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(15,25,55,0.35)', zIndex: 1200,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 680, maxWidth: '95vw', maxHeight: '85vh', background: '#fff',
        borderRadius: 16, boxShadow: '0 30px 80px rgba(15,25,55,0.35)',
        zIndex: 1201, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #eef2f8',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #f8faff 0%, #eef4ff 100%)',
          borderRadius: '16px 16px 0 0',
        }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase',
              letterSpacing: 1, padding: '3px 10px', borderRadius: 999,
              background: '#fff', border: '1px solid #e0e7ff', marginBottom: 6,
            }}>
              🧊 Chamber Aliases
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: theme.textPrimary }}>
              Rename Chamber Codes (per warehouse)
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
              Pick an icon and a description for each chamber. Saved on the server — all users see these.
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: '#eef2f8', width: 32, height: 32, borderRadius: 8,
            cursor: 'pointer', fontSize: 16, fontWeight: 700, color: theme.textSecondary,
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 24px', overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f6fb', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', width: 190, borderRadius: '8px 0 0 8px' }}>Code</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', width: 160 }}>Icon</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '10px 12px', width: 36, borderRadius: '0 8px 8px 0' }}></th>
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>No chambers loaded yet.</td></tr>
              )}
              {codes.map(code => (
                <tr key={code} style={{ borderTop: '1px solid #f3f6fb' }}>
                  <td style={{
                    padding: '10px 12px', fontWeight: 800,
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    color: theme.textPrimary, fontSize: 12,
                  }}>
                    <span style={{
                      background: '#eef2ff', padding: '3px 8px', borderRadius: 6,
                      border: '1px solid #e0e7ff', color: '#4338ca',
                      display: 'inline-block', maxWidth: 170,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{code}</span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ChamberIcon icon={iconDraft[code] || ''} />
                      <select
                        value={iconDraft[code] || ''}
                        onChange={(e) => setIconDraft(d => ({ ...d, [code]: e.target.value }))}
                        style={{
                          flex: 1, padding: '6px 8px', fontSize: 12,
                          border: '1px solid #d8dee9', borderRadius: 6,
                          outline: 'none', background: '#fff', cursor: 'pointer',
                          fontFamily: 'inherit', color: theme.textPrimary,
                        }}
                      >
                        {CHAMBER_ICONS.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <input
                      value={descDraft[code] || ''}
                      onChange={(e) => setDescDraft(d => ({ ...d, [code]: e.target.value }))}
                      placeholder="e.g. Temperature Controlled"
                      style={{
                        width: '100%', padding: '8px 12px', fontSize: 13,
                        border: '1px solid #d8dee9', borderRadius: 8,
                        outline: 'none', fontFamily: 'inherit', color: theme.textPrimary,
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#d8dee9'; }}
                    />
                  </td>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                    {(iconDraft[code] || descDraft[code]) && (
                      <button onClick={() => clearRow(code)} title="Clear" style={{
                        border: 'none', background: '#fef2f2', color: '#dc2626',
                        cursor: 'pointer', fontSize: 13, width: 26, height: 26,
                        borderRadius: 6, fontWeight: 700,
                      }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #eef2f8',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#fafbfd', borderRadius: '0 0 16px 16px',
        }}>
          <button onClick={clearAll} style={{
            padding: '9px 16px', borderRadius: 8, border: '1px solid #fecaca',
            background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: 12,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Clear all</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '9px 16px', borderRadius: 8, border: '1px solid #e5eaf2',
              background: '#fff', color: theme.textSecondary, fontWeight: 600, fontSize: 12,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              background: saving ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5, #6366f1)',
              color: '#fff', fontWeight: 700, fontSize: 12, cursor: saving ? 'default' : 'pointer',
              fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
            }}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChamberRenameModal;
